import { useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, QrCode, Trash } from '@phosphor-icons/react'
import { isApiError } from '@/api'
import { istDateKey } from '@/lib/ist'
import { Banner, Button, Field, IconButton, Input, Modal, Select } from '@/ui'
import { errorDetail, errorMessage } from '../../api/core'
import { arr, obj, str } from '../../api/mappers'
import { useUploadLabReport, type LabReportSaved } from '../../api/lab'
import { useOrgs } from '../../api/reference'
import {
  emptyLabReportDraft,
  emptyLabRow,
  labReportFormData,
  labReportOk,
  readableMissing,
  validateLabReport,
  type LabReportDraft,
  type LabRowDraft,
} from '../../model/lab/labReportForm'
import { LAB_WARNING_LABEL } from '../../model/lab/labText'
import { humanize } from '../../labels'
import { Code, Status } from '../bits'

const PANELS = ['PESTICIDE', 'AFLATOXIN', 'HEAVY_METALS', 'MICROBIAL']

/**
 * Attach a lab report (F7.5-e): the PDF as received, the lab's results file or typed rows. The
 * server flags every row against the limit in force on the test date; a failing row puts stock on
 * hold at once. After saving, the result (overall, warnings, stock held) stays on screen.
 */
export function LabReportUploadDialog({ onClose, initial }: { onClose: () => void; initial?: Partial<LabReportDraft> }) {
  const [d, setD] = useState<LabReportDraft>(() => ({ ...emptyLabReportDraft(), ...initial }))
  const [touched, setTouched] = useState(false)
  const [saved, setSaved] = useState<LabReportSaved | null>(null)
  const labs = useOrgs('LAB')
  const upload = useUploadLabReport()
  const today = istDateKey()
  const p = validateLabReport(d, today)
  const ok = labReportOk(p)
  const show = <T,>(v: T) => (touched ? v : undefined)

  const save = async () => {
    setTouched(true)
    if (!ok) return
    try {
      setSaved(await upload.mutateAsync(labReportFormData(d)))
    } catch {
      /* shown below */
    }
  }

  const setRow = (i: number, patch: Partial<LabRowDraft>) =>
    setD({ ...d, rows: d.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) })

  if (saved) return <SavedResult saved={saved} reportNo={d.reportNo} onClose={onClose} />

  return (
    <Modal
      open
      onClose={onClose}
      dismissible={!upload.isPending}
      title="Attach lab report"
      description="The report PDF exactly as the lab sent it, and its results. A second person checks the rows against the PDF afterwards."
      footer={
        <>
          <Button variant="ghost" className="ml-auto" onClick={onClose} disabled={upload.isPending}>
            Cancel
          </Button>
          <Button loading={upload.isPending} onClick={() => void save()}>
            Save report
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        {upload.isError && <UploadProblem error={upload.error} />}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Sample seal" required error={show(p.sample)} hint="Scan the seal on the sample bag, or type its number.">
            <Input
              value={d.sealPayload}
              onChange={(e) => setD({ ...d, sealPayload: e.target.value, sampleId: '' })}
              leading={<QrCode />}
              className="font-mono"
              maxLength={64}
              data-autofocus
            />
          </Field>
          <Field label="Report number" required error={show(p.reportNo)}>
            <Input value={d.reportNo} onChange={(e) => setD({ ...d, reportNo: e.target.value })} placeholder="LR-2026-1108" className="font-mono" maxLength={40} />
          </Field>
          <Field label="Lab" hint="Empty: the lab the sample was sent to.">
            <Select
              value={d.labOrgId}
              onChange={(labOrgId) => setD({ ...d, labOrgId })}
              placeholder={labs.isLoading ? 'Loading…' : 'The lab the sample was sent to'}
              options={[{ value: '', label: 'The lab the sample was sent to' }, ...(labs.data ?? []).map((o) => ({ value: o.id, label: o.name }))]}
            />
          </Field>
          <Field label="Accreditation number" error={show(p.accreditationNo)} hint="Empty: the number on file for the lab.">
            <Input value={d.accreditationNo} onChange={(e) => setD({ ...d, accreditationNo: e.target.value })} className="font-mono" maxLength={40} />
          </Field>
          <Field label="Test date" required error={show(p.testedAt)}>
            <Input type="date" value={d.testedAt} max={today} onChange={(e) => setD({ ...d, testedAt: e.target.value })} />
          </Field>
          <Field label="Received" error={show(p.receivedAt)} hint="Empty: now.">
            <Input type="datetime-local" value={d.receivedAt} onChange={(e) => setD({ ...d, receivedAt: e.target.value })} />
          </Field>
          <Field label="Report PDF" required error={show(p.pdf)} hint="The file as received, not a photo.">
            <Input type="file" accept="application/pdf,.pdf" onChange={(e) => setD({ ...d, pdf: e.target.files?.[0] ?? null })} />
          </Field>
          <Field label="Results file" error={show(p.resultsFile)} hint="The lab's CSV or spreadsheet. Without it, type the rows below.">
            <Input
              type="file"
              accept=".csv,.xlsx,.xls,.ods,text/csv"
              onChange={(e) => setD({ ...d, resultsFile: e.target.files?.[0] ?? null })}
            />
          </Field>
        </div>

        {!d.resultsFile && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-[14px] font-semibold text-ink">Results</legend>
            <p className="text-[13px] text-muted">
              One row per substance. Leave the result empty, or write ND or &lt;LOQ, when it was not detected.
            </p>
            {touched && p.rows && <p className="text-[13px] font-medium text-danger">{p.rows}</p>}
            <datalist id="lab-panels">
              {PANELS.map((x) => (
                <option key={x} value={x} />
              ))}
            </datalist>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-[14px]">
                <caption className="sr-only">Results rows</caption>
                <thead>
                  <tr className="text-left font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
                    <th scope="col" className="py-1 pr-1">Test</th>
                    <th scope="col" className="px-1 py-1">Substance</th>
                    <th scope="col" className="px-1 py-1">Result</th>
                    <th scope="col" className="px-1 py-1">Unit</th>
                    <th scope="col" className="px-1 py-1">LOQ</th>
                    <th scope="col" className="px-1 py-1">Method</th>
                    <th scope="col" className="py-1"><span className="sr-only">Remove</span></th>
                  </tr>
                </thead>
                <tbody>
                  {d.rows.map((r, i) => {
                    const err = touched ? p.rowErrors?.[i] : undefined
                    return (
                      <RowFragment key={i} error={err}>
                        <td className="py-1 pr-1">
                          <Input aria-label={`Row ${i + 1} test`} list="lab-panels" value={r.panel} onChange={(e) => setRow(i, { panel: e.target.value })} className="h-9 font-mono text-[13px]" invalid={Boolean(err)} />
                        </td>
                        <td className="px-1 py-1">
                          <Input aria-label={`Row ${i + 1} substance`} value={r.analyte} onChange={(e) => setRow(i, { analyte: e.target.value })} className="h-9 text-[13px]" invalid={Boolean(err)} />
                        </td>
                        <td className="px-1 py-1">
                          <Input aria-label={`Row ${i + 1} result`} value={r.value} onChange={(e) => setRow(i, { value: e.target.value })} placeholder="ND" className="h-9 font-mono text-[13px]" invalid={Boolean(err)} />
                        </td>
                        <td className="px-1 py-1">
                          <Input aria-label={`Row ${i + 1} unit`} value={r.uom} onChange={(e) => setRow(i, { uom: e.target.value })} placeholder="mg/kg" className="h-9 w-24 text-[13px]" invalid={Boolean(err)} />
                        </td>
                        <td className="px-1 py-1">
                          <Input aria-label={`Row ${i + 1} LOQ`} inputMode="decimal" value={r.loq} onChange={(e) => setRow(i, { loq: e.target.value })} placeholder="0.01" className="h-9 w-24 font-mono text-[13px]" invalid={Boolean(err)} />
                        </td>
                        <td className="px-1 py-1">
                          <Input aria-label={`Row ${i + 1} method`} value={r.method} onChange={(e) => setRow(i, { method: e.target.value })} className="h-9 text-[13px]" />
                        </td>
                        <td className="py-1 text-right">
                          <IconButton
                            label={`Remove row ${i + 1}`}
                            icon={<Trash />}
                            size="sm"
                            onClick={() => setD({ ...d, rows: d.rows.length > 1 ? d.rows.filter((_, j) => j !== i) : [emptyLabRow()] })}
                          />
                        </td>
                      </RowFragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <Button
              size="sm"
              variant="secondary"
              className="w-fit"
              leadingIcon={<Plus />}
              onClick={() => setD({ ...d, rows: [...d.rows, emptyLabRow(d.rows[d.rows.length - 1]?.panel ?? '')] })}
            >
              Add row
            </Button>
          </fieldset>
        )}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  )
}

function RowFragment({ children, error }: { children: ReactNode; error?: string }) {
  return (
    <>
      <tr className="align-top">{children}</tr>
      {error && (
        <tr>
          <td colSpan={7} className="pb-1 text-[12.5px] font-medium text-danger">
            {error}
          </td>
        </tr>
      )}
    </>
  )
}

/** The server's refusal, with its lists (missing fields, analytes without a limit). */
function UploadProblem({ error }: { error: unknown }) {
  const code = isApiError(error) ? error.code : undefined
  const errors = obj(errorDetail(error).errors)
  const missing = arr<unknown>(errors.missing).map(String)
  const analytes = arr<unknown>(errors.analytes).map(String)
  const existing = str(errors.lab_report_id, '')
  return (
    <Banner tone="error" title={errorMessage(error)}>
      {missing.length > 0 && (
        <ul className="mt-1 list-disc pl-5 text-ink">
          {missing.map((m) => (
            <li key={m}>{readableMissing(m)}</li>
          ))}
        </ul>
      )}
      {analytes.length > 0 && (
        <>
          <ul className="mt-1 list-disc pl-5 text-ink">
            {analytes.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
          <Link to="/c/lab-limits" className="mt-1 inline-block underline">
            Open lab limits
          </Link>
        </>
      )}
      {code === 'REPORT_ALREADY_STORED' && existing && (
        <Link to={`/c/lab-reports/${existing}`} className="mt-1 inline-block underline">
          Open the stored report
        </Link>
      )}
    </Banner>
  )
}

function SavedResult({ saved, reportNo, onClose }: { saved: LabReportSaved; reportNo: string; onClose: () => void }) {
  const navigate = useNavigate()
  const notPass = saved.results.filter((r) => r.flag !== 'PASS')
  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span>
          Report <Code className="text-[inherit]">{reportNo}</Code> saved
        </span>
      }
      footer={
        <>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={() => {
              onClose()
              navigate(`/c/lab-reports/${saved.id}`)
            }}
          >
            Open report
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[14px]">
        <p className="flex items-center gap-2">
          Overall <Status status={saved.overall} />
          <span className="text-muted">· waiting for a second person to check the rows</span>
        </p>
        {saved.supersedesId && <p className="text-muted">It replaces the earlier report with the same number.</p>}
        {saved.warnings.map((w) => (
          <Banner key={w} tone="warn">
            {LAB_WARNING_LABEL[w] ?? humanize(w)}
          </Banner>
        ))}
        {saved.heldUnitIds.length > 0 && (
          <Banner tone="error" title={`${saved.heldUnitIds.length} ${saved.heldUnitIds.length === 1 ? 'lot or batch' : 'lots and batches'} put on hold`}>
            Everything made from the failing stock is held until the case is decided.
            {saved.exceptionId && (
              <Link to={`/c/exceptions?open=${saved.exceptionId}`} className="ml-1 underline">
                Open the problem
              </Link>
            )}
          </Banner>
        )}
        {notPass.length > 0 && (
          <ul className="flex flex-col gap-1">
            {notPass.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2">
                <Status status={r.flag} />
                <span>
                  {humanize(r.panel)} · {r.analyte}: {r.text}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  )
}
